import {
  BottomSheet,
  DialogFooter,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Checkbox,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { APP_V2_APP_EVENTS, ExternalLink, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import { jobTypeOptions, shiftTypeOptions } from "../../PlacementCandidate/constants";
import { type PlacementCandidate } from "../../PlacementCandidate/types";
import { useCreateHireReport } from "../api/useCreateHireReport";
import { useGetEngagedWorkplaces } from "../api/useGetEngagedWorkplaces";

interface GetAJobModalProps {
  modalState: UseModalState;
  placementId: string;
  placementCandidate: PlacementCandidate;
}

const hireReportSchema = z.object({
  workplaceId: z.string(),
  startDate: z.date(),
  jobType: z.string().min(1, "Please select a role type"),
  shiftTypes: z.array(z.string()).min(1, "Please select at least one shift type"),
});
type HireReport = z.infer<typeof hireReportSchema>;

export function GetAJobModal(props: GetAJobModalProps) {
  const { modalState, placementId, placementCandidate } = props;
  const { showSuccessToast, showErrorToast } = useToast();
  const { placementClaimSignOnBonusSupportLink } = useSupportLinks();
  const { mutateAsync: createHireReport, isLoading: isCreateHireReportLoading } =
    useCreateHireReport();
  const {
    data: workplaces,
    isLoading: isGetWorkplacesLoading,
    isSuccess: isGetWorkplacesSuccess,
  } = useGetEngagedWorkplaces({
    placementCandidateId: placementCandidate.id,
  });
  const workplaceJobForm = useForm<HireReport>({
    resolver: zodResolver(hireReportSchema),
  });
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
  } = workplaceJobForm;

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_GET_A_JOB_MODAL_VIEWED, {
    placementCandidateId: placementCandidate.id,
    placementId,
  });

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter
          orientation="horizontal"
          LinkComponent={Link}
          onClose={() => {
            modalState.closeModal();
          }}
        >
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            type="submit"
            isLoading={isCreateHireReportLoading || isGetWorkplacesLoading}
            form="workplace-hire-report-form"
          >
            Submit
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={6}>
          <Title component="h3" variant="h4">
            Congratulations on your new job!
          </Title>
          <form
            id="workplace-hire-report-form"
            onSubmit={handleSubmit(async (formData) => {
              try {
                await createHireReport({
                  placementCandidateId: placementCandidate.id,
                  workplaceId: formData.workplaceId,
                  startDate: formData.startDate,
                  jobType: formData.jobType,
                  shiftTypes: formData.shiftTypes,
                  placementId,
                });
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_GET_A_JOB_MODAL_SUBMITTED, {
                  placementCandidateId: placementCandidate.id,
                  workplaceId: formData.workplaceId,
                  startDate: formData.startDate,
                  jobType: formData.jobType,
                  shiftTypes: formData.shiftTypes,
                  placementId,
                });
                showSuccessToast(`Your job was reported successfully`);
                modalState.closeModal();
              } catch {
                showErrorToast("Something went wrong, please contact tech team.");
              }
            })}
          >
            <Stack spacing={5}>
              <FormControl fullWidth component={Stack} spacing={2}>
                <FormLabel htmlFor="name">Workplace Name</FormLabel>
                <Controller
                  name="workplaceId"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} fullWidth displayEmpty>
                      {isGetWorkplacesSuccess
                        ? workplaces.data.map((workplace) => (
                            <MenuItem key={workplace.id} value={workplace.id}>
                              {workplace.attributes.name}
                            </MenuItem>
                          ))
                        : undefined}
                    </Select>
                  )}
                />
                <FormHelperText>{formErrors.workplaceId?.message}</FormHelperText>
              </FormControl>
              <FormControl fullWidth component={Stack} spacing={2}>
                <FormLabel htmlFor="name">Orientation Start Date</FormLabel>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => <DatePicker {...field} />}
                />
                <FormHelperText>{formErrors.startDate?.message}</FormHelperText>
              </FormControl>
              <FormControl fullWidth component={Stack} spacing={2}>
                <FormLabel>What type of role is it?</FormLabel>
                <Controller
                  name="jobType"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <RadioGroup {...field} row>
                      {jobTypeOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio />}
                          label={option.label}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <FormHelperText>{formErrors.jobType?.message}</FormHelperText>
              </FormControl>
              <FormControl fullWidth component={Stack} spacing={2}>
                <FormLabel>What shift you work?</FormLabel>
                <Controller
                  name="shiftTypes"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <Stack direction="row" gap={2}>
                      {shiftTypeOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Checkbox
                              checked={field.value.includes(option.value)}
                              onChange={(event) => {
                                const newValue = event.target.checked
                                  ? [...field.value, option.value]
                                  : field.value.filter((s) => s !== option.value);
                                field.onChange(newValue);
                              }}
                            />
                          }
                          label={option.label}
                        />
                      ))}
                    </Stack>
                  )}
                />
                <FormHelperText>{formErrors.shiftTypes?.message}</FormHelperText>
              </FormControl>
              <Text variant="body1">
                We&apos;ll send the gift after you complete your first week of orientation.{" "}
                <ExternalLink to={placementClaimSignOnBonusSupportLink}>
                  Read about the gift.
                </ExternalLink>
              </Text>
            </Stack>
          </form>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
